import type { ReactNode } from 'react';
import { T, Var } from 'gt-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BulkActionsBarProps {
  /** Number of selected rows. The bar renders nothing when it is 0. */
  selectedCount: number;
  /** Clears the table's row selection (the bar's trailing Clear button). */
  onClear: () => void;
  /** The caller's bulk action buttons, rendered after the count. */
  children: ReactNode;
  className?: string;
}

/**
 * Toolbar shown above a table while rows are selected: the selection count,
 * the caller's bulk action buttons, and a trailing Clear button. Shared so
 * every table's bulk bar looks and behaves the same — render it
 * unconditionally and it takes care of hiding itself when nothing is
 * selected.
 */
export function BulkActionsBar({
  selectedCount,
  onClear,
  children,
  className,
}: BulkActionsBarProps) {
  if (selectedCount === 0) return null;
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg bg-muted px-3 py-2',
        className,
      )}
    >
      <span className="text-sm text-muted-foreground">
        <T context="count of selected table rows">
          <Var>{selectedCount}</Var> selected
        </T>
      </span>
      {children}
      <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
        <T context="button, clear the table row selection">Clear</T>
      </Button>
    </div>
  );
}
